import React from 'react';
import { Autocomplete, TextField, FormHelperText } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import type { AutocompleteOwnerState, AutocompleteRenderOptionState } from '@mui/material';
import AutocompleteContainer from './CustomAutocomplete.style';

type PropsType<T> = {
  isTouched?: boolean;
  multiple?: boolean;
  nameAutocomplete: string;
  value: T[];
  options: T[];
  errorText?: string;
  placeholder?: string;
  className?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  isDisabled?: boolean;
  autoFocus?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChangeValue?: (value: T[] | T) => void;
  onClick?: () => void;
  groupBy?: (option: T) => string;
  getOptionLabel: (option: T) => string;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    state?: AutocompleteRenderOptionState,
    ownerState?: AutocompleteOwnerState<T, boolean, false, false, 'div'>
  ) => React.ReactNode;
};

// eslint-disable-next-line @typescript-eslint/comma-dangle
const CustomAutocomplete = <T, >({
  isTouched,
  multiple,
  nameAutocomplete,
  value,
  options,
  errorText,
  placeholder,
  className,
  isDisabled,
  autoFocus,
  onBlur,
  onChangeValue,
  onClick,
  groupBy,
  getOptionLabel,
  renderOption,
}: PropsType<T>) => {
  const isErrorDisplay = isTouched && Boolean(errorText);

  return (
    <AutocompleteContainer onClick={onClick} className={className}>
      <Autocomplete
        id={nameAutocomplete}
        multiple={multiple}
        value={value}
        options={options}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel}
        filterSelectedOptions
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              placeholder,
              endAdornment: (
                <React.Fragment>
                  {!options.length ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            error={isErrorDisplay}
          />
        )}
        onChange={(_, value) => {
          if (onChangeValue && value) onChangeValue(value);
        }}
        renderOption={renderOption}
        onBlur={onBlur}
        disabled={isDisabled}
        autoFocus={autoFocus}
        loading
        loadingText="Loading..."
      />

      {isErrorDisplay && <FormHelperText error={isErrorDisplay}>{errorText}</FormHelperText>}
    </AutocompleteContainer>
  );
};

export default CustomAutocomplete;
